// The Aurelia API as this app's server forwards it: REST under /api, live data on /live. The shapes
// are the API's (https://app.aurelia.build/llms-full.txt), trimmed to the fields the app shows.
import { useEffect, useRef, useState } from "react";

export type ChainId = "solana" | "evm:56" | "evm:8453" | "evm:1" | "evm:4663";
// In the order the chain pickers show them. `explorerName` is what the explorer link's title names.
export const CHAINS: Record<ChainId, { name: string; slug: string; explorer: string; explorerName: string; launchpads: string[] }> = {
  solana: { name: "Solana", slug: "solana", explorer: "https://solscan.io", explorerName: "Solscan", launchpads: ["pumpfun", "letsbonk", "raydium-launchlab", "meteora-dbc"] },
  "evm:56": { name: "BSC", slug: "bsc", explorer: "https://bscscan.com", explorerName: "BscScan", launchpads: ["fourmeme", "flap", "genius"] },
  "evm:8453": { name: "Base", slug: "base", explorer: "https://basescan.org", explorerName: "BaseScan", launchpads: ["doppler", "zora", "clanker", "virtuals", "o1", "basestonk", "flaunch", "rwagmi"] },
  "evm:1": { name: "Ethereum", slug: "ethereum", explorer: "https://etherscan.io", explorerName: "Etherscan", launchpads: ["clanker", "stockereum"] },
  "evm:4663": { name: "Robinhood Chain", slug: "robinhood", explorer: "https://robinhoodchain.blockscout.com", explorerName: "Blockscout", launchpads: ["flap", "doppler", "pons", "pools-trade", "torch", "frontier"] },
};
export const CHAIN_IDS = Object.keys(CHAINS) as ChainId[];
export const chainBySlug = (slug: string) => (Object.keys(CHAINS) as ChainId[]).find((c) => CHAINS[c].slug === slug);
export const tokenHref = (chain: ChainId, address: string) => `/${CHAINS[chain].slug}/${address}`;
export const walletHref = (chain: ChainId, address: string) => `/${CHAINS[chain].slug}/wallet/${address}`;
export const explorerTx = (chain: ChainId, tx: string) => `${CHAINS[chain].explorer}/tx/${tx}`;
export const explorerAccount = (chain: ChainId, a: string) =>
  `${CHAINS[chain].explorer}/${chain === "solana" ? "account" : "address"}/${a}`;
export const explorerToken = (chain: ChainId, a: string) => `${CHAINS[chain].explorer}/token/${a}`;

export const LAUNCHPADS: Record<string, string> = {
  pumpfun: "pump.fun",
  letsbonk: "letsbonk",
  "raydium-launchlab": "LaunchLab",
  "meteora-dbc": "Meteora DBC",
  fourmeme: "four.meme",
  flap: "flap.sh",
  genius: "Genius",
  doppler: "Doppler",
  zora: "Zora",
  clanker: "Clanker",
  virtuals: "Virtuals",
  o1: "o1",
  basestonk: "BaseStonk",
  flaunch: "Flaunch",
  stockereum: "Stockereum",
  pons: "Pons",
  rwagmi: "Rwagmi",
  "pools-trade": "pools.trade",
  torch: "Torch",
  frontier: "Frontier",
};

export type Socials = { website: string | null; twitter: string | null; telegram: string | null; discord: string | null };

export type Window = {
  priceChange: number | null;
  volumeUsd: number;
  buyVolumeUsd: number;
  sellVolumeUsd: number;
  trades: number;
  buys: number;
  sells: number;
  traders: number;
  buyers?: number;
  sellers?: number;
};

export type FeeWindow = "5m" | "15m" | "30m" | "45m" | "1h" | "6h" | "24h";

export type TokenRow = {
  chain: ChainId;
  token: { address: string; symbol: string | null; name: string | null; logo: string | null };
  priceUsd: number | null;
  change: { "5m": number | null; "1h": number | null; "6h": number | null; "24h": number | null };
  volume24hUsd: number;
  trades24h: number;
  buys24h: number;
  sells24h: number;
  traders24h: number;
  feesPaidUsd: Record<FeeWindow, number | null>;
  marketCapUsd: number | null;
  fdvUsd: number | null;
  liquidityUsd: number | null;
  createdAt: number | null;
  launchpad: { id: string; progress: number | null; complete: boolean } | null;
  flags: string[] | null;
  peakMarketCapUsd?: number | null; // with `creator=`
};

export type Pool = {
  address: string;
  protocol: string;
  counter: { address: string; symbol: string | null };
  priceUsd: number | null;
  liquidityUsd: number | null;
  volume24hUsd: number;
  trades24h: number;
};

export type Security = {
  flags: string[];
  unknown?: Record<string, string>;
  honeypot?: { likely: boolean | null; buyers: number; sellers: number } | null;
  lp?: { burnedShare: number | null; lockedShare: number | null } | null;
  top10Share?: number | null;
  mintAuthority?: string | null;
  freezeAuthority?: string | null;
  metadata?: { mutable: boolean | null } | null;
  contract?: { renounced: boolean | null; proxy?: { upgradeable: boolean } | null } | null;
  taxes?: { buy: number | null; sell: number | null } | null;
};

export type TokenPage = {
  token: {
    address: string;
    symbol: string | null;
    name: string | null;
    decimals: number;
    totalSupply: string | null;
    circulatingSupply: string | null;
    createdAt: number | null;
    creator: string | null;
    logo: string | null;
    description: string | null;
    socials: Socials;
    security: Security | null;
  };
  holders: { count: number | null; top10Share: number | null } | null;
  creator: { address: string; labels: string[]; tokensCreated: number; graduated: number } | null;
  price: { usd: number | null; quote: number; quoteToken: { symbol: string | null } | null; pool: string; time: number } | null;
  windows: Record<"5m" | "1h" | "6h" | "24h", Window>;
  chartPool: string | null;
  poolCount: number;
  liquidityUsd: number | null;
  marketCapUsd: number | null;
  fdvUsd: number | null;
  pools: Pool[];
  ath: { usd: number; at: number } | null;
  atl: { usd: number; at: number } | null;
  bonding: { progress: number | null; complete: boolean } | null;
  latestTradeAt: number | null;
};

export type Trade = {
  time: number;
  block: number;
  txIndex: number;
  eventIndex: number;
  tx: string;
  pool: string;
  protocol: string;
  side: "buy" | "sell";
  token: { address: string; symbol: string | null };
  amount: string;
  counter: { symbol: string | null };
  counterAmount: string;
  priceUsd: number | null;
  amountUsd: number | null;
  trader: string;
  labels: string[];
  preconfirmed?: true;
};

export type Bar = { t: number; o: number; h: number; l: number; c: number; v: number; n: number };

export type PulseToken = {
  address: string;
  symbol: string | null;
  name: string | null;
  logo: string | null;
  launchpad: string;
  createdAt: number;
  socials: Socials;
  priceUsd: number | null;
  marketCapUsd: number | null;
  liquidityUsd: number | null;
  progress: number | null;
  graduatedAt: number | null;
  latestTradeAt: number | null;
  creatorNetShare: number | null;
  sniperCount: number | null;
  sniperNetShare: number | null;
  windows: Record<"5m" | "1h" | "6h" | "24h", Window>;
};

export type ChainStatus = {
  chain: ChainId;
  lag: number;
  indexedSince: number;
  stats24h: { volumeUsd: number; trades: number; traders: number; newTokens: number; newPools: number };
};

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`/api${path}`, { signal });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, body?.error ?? res.statusText);
  return body as T;
}

/** A GET kept current: loads on mount and when `path` changes, then every `every` ms while the tab is visible. */
export function useGet<T>(path: string | null, every?: number) {
  const [state, setState] = useState<{ data: T | null; error: ApiError | null; path: string | null }>({ data: null, error: null, path: null });
  useEffect(() => {
    if (!path) return;
    const ctl = new AbortController();
    const load = () =>
      get<T>(path, ctl.signal).then(
        (data) => setState({ data, error: null, path }),
        (e) => !ctl.signal.aborted && setState((s) => ({ data: s.path === path ? s.data : null, error: e instanceof ApiError ? e : new ApiError(0, String(e)), path })),
      );
    load();
    const timer = every ? setInterval(() => document.visibilityState === "visible" && load(), every) : undefined;
    return () => {
      ctl.abort();
      clearInterval(timer);
    };
  }, [path, every]);
  const current = state.path === path;
  return { data: current ? state.data : null, error: current ? state.error : null, loading: !current };
}

// ─── Live: one socket for the page, each subscription routed by its id ───

export type LiveMsg = { id?: string; type: string; data?: unknown; status?: number; error?: string; [k: string]: unknown };
type Params = Record<string, string | number>;

const handlers = new Map<string, { params: Params; on: (m: LiveMsg) => void }>();
let socket: WebSocket | null = null;
let delay = 1000;
let seq = 0;

function connect() {
  const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/live`);
  socket = ws;
  ws.onopen = () => {
    for (const [id, h] of handlers) ws.send(JSON.stringify({ op: "subscribe", id, ...h.params }));
  };
  ws.onmessage = (e) => {
    const m = JSON.parse(e.data) as LiveMsg;
    if (m.type === "subscribed") delay = 1000;
    if (m.type === "resubscribed") for (const h of handlers.values()) h.on(m);
    else if (m.id) handlers.get(m.id)?.on(m);
  };
  ws.onclose = () => {
    socket = null;
    setTimeout(connect, delay);
    delay = Math.min(delay * 2, 30_000);
  };
}

export function live(params: Params, on: (m: LiveMsg) => void): () => void {
  const id = `c${++seq}`;
  handlers.set(id, { params, on });
  if (!socket) connect();
  else if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ op: "subscribe", id, ...params }));
  return () => {
    handlers.delete(id);
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ op: "unsubscribe", id }));
  };
}

/** Follows `params` while mounted (null: nothing). `on` may change between renders. */
export function useLive(params: Params | null, on: (m: LiveMsg) => void) {
  const ref = useRef(on);
  ref.current = on;
  const key = params ? JSON.stringify(params) : null;
  useEffect(() => {
    if (!key) return;
    return live(JSON.parse(key), (m) => ref.current(m));
  }, [key]);
}

/** The round trip to this site's server in ms, every 5 s while the tab is in view; null until it answers, or when it does not. */
export function usePing(): number | null {
  const [ms, setMs] = useState<number | null>(null);
  useEffect(() => {
    const measure = async () => {
      if (document.visibilityState !== "visible") return;
      const start = performance.now();
      try {
        const res = await fetch("/health", { cache: "no-store", signal: AbortSignal.timeout(4000) });
        setMs(res.ok ? Math.round(performance.now() - start) : null);
      } catch {
        setMs(null);
      }
    };
    measure();
    const timer = setInterval(measure, 5000);
    return () => clearInterval(timer);
  }, []);
  return ms;
}

/** Re-renders every `ms`, for ages that count up. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
